import React, { useMemo } from 'react';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import getTabBarIcon from '@shared/components/navigation/TabBarIcon';
import { MODULE_ROUTES, ModuleRoute } from './routeRegistry';
import tabNavigatorStyle from '@assets/styles/tabNavigatorStyles';
import { colorPalette } from '@shared/config/colorsConfig';
import { WebSocketMeasurementProvider } from '@shared/providers/WebSocketMeasurementProvider';
import { webSocketConfig } from '@shared/config/webSocketConfig';
import { useUser } from 'app/auth/contexts/UserContext';

const Tab = createBottomTabNavigator();
function sortAndFilterTabRoutes(routes: ModuleRoute[]) {
  return routes
    .filter(r => r.meta.placement === 'tab')
    .sort((a, b) => (a.meta.order ?? 100) - (b.meta.order ?? 100));
}
export const AppTabs: React.FC = () => {
  const tabRoutes = useMemo(() => sortAndFilterTabRoutes(MODULE_ROUTES), []);
  const insets = useSafeAreaInsets();
  const { meData } = useUser();
  // On web there is no safe-area inset, so the bar is shorter than the design
  // assumes and the below-icon labels get clipped. Give web a taller bar with
  // looser padding so icon + label fit.
  const isWeb = Platform.OS === 'web';

  const webSocketUrl = meData?.current_pathway?.id
    ? webSocketConfig.getPatientPathwayUrl(meData.current_pathway.id)
    : '';

  return (
    <WebSocketMeasurementProvider url={webSocketUrl}>
    <Tab.Navigator

      screenOptions={({ route }) => ({
        headerShown: false,
        tabBarIcon: ({ focused, color, size }) => getTabBarIcon(route.name, focused, color, size),
        tabBarActiveTintColor: colorPalette.secondary[600],
        tabBarInactiveTintColor: colorPalette.gray[500],
        tabBarStyle: {
          ...tabNavigatorStyle.tabBar,
          height: (isWeb ? 96 : 90) + (isWeb ? 0 : insets.bottom),
          paddingTop: isWeb ? 10 : 20,
          paddingBottom: (isWeb ? 14 : 20) + (isWeb ? 0 : insets.bottom),
        },
        tabBarItemStyle: { ...tabNavigatorStyle.tabBarItem, paddingVertical: 4 },
        tabBarLabelPosition: 'below-icon',
        tabBarLabelStyle: tabNavigatorStyle.tabBarLabel,
        animation: 'fade',
        tabBarSafeAreaInsets: { bottom: 0 },
        animationDuration: 250,
      })}>
      {tabRoutes.map(({ id, Stack, meta }) => (
        <Tab.Screen
          key={id}
          name={id}
          component={Stack}
          options={{
            tabBarLabel: meta.tabLabel ?? id,
            tabBarIcon: ({ color, size, focused }) => {
              if (typeof meta.tabIcon === 'function') {
                const Icon = meta.tabIcon;
                return <Icon color={color} size={size} focused={focused} />;
              }
              return getTabBarIcon(meta.tabIcon, focused, color, size);
            },
            ...tabNavigatorStyle.tabBarItem
          }}
        />
      ))}
    </Tab.Navigator>
    </WebSocketMeasurementProvider>
  );
};

export default AppTabs;
